import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { get, isEmpty } from 'lodash';
import {
  withStyles,
  Button,
  Link,
  Tooltip, Typography,
} from '@material-ui/core';
import LensIcon from '@material-ui/icons/Lens';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import AutorenewIcon from '@material-ui/icons/Autorenew';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'

import AlertDialog from '../../../../../components/AlertDialog';
import { getDeployedStatusColor, getDeployedStatusLabel } from '../../../../../utils/deployment';
import { formatStatusMessage } from '../../../../../utils';
import {
  DEPLOYMENT_ACTION_TYPE_DEPLOY,
  DEPLOYMENT_ACTION_TYPE_REDEPLOY,
  DEPLOYMENT_ACTION_TYPE_UNDEPLOY,
  DEPLOYMENT_STATUS_ERROR,
  DEPLOYMENT_STATUS_NOT_DEPLOYED,
  DEPLOYMENT_STATUS_UNDEPLOYED_ERROR,
} from '../../constants';
import styles from '../styles';
import { getI18n, getI18nFormattedMessage } from '../../../../../utils/intl';
import ListContainer from "../../../../list";
import { FilterByName, FilterByDeploymentState } from "../controls";

export const DeploymentTable = ({
  deploymentProxyList,
  gatewayBundleDetails,
  deploymentErrors,
  refreshTime = 10,
  deployGatewayBundle,
  redeployGatewayBundle,
  undeployGatewayBundle,
  checkStatus,
  classes,
  filterByState,
  onFilterByStateChange,
  filterByName,
  onFilterByNameChange,
  onFilterByNameKeyPress,
  page,
  totalElements,
  totalPages,
  rowsPerPage,
  onChangeRowsPerPage,
  onChangePage,
  onChangePreviousPage,
  onChangeNextPage,
}) => {

  const intl = getI18n(useIntl());
  const { getI18nMessage } = intl;
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState('');
  const [proxyUpdating, setProxyUpdating] = useState('');
  const [timer, setTimer] = useState(parseInt(refreshTime, 10));
  const [selectedProxy, setSelectedProxy] = useState();
  const latestGatewayBundleVersion = get(gatewayBundleDetails, 'version');
  const pageId = 'gateway-bundle-details-deployments'

  const getColumns = () => [
    {
      id: 'proxyName',
      label: getI18nFormattedMessage('label.gateway.bundle.details.page.deployments.proxy.title'),
      value: renderProxyName,
      minWidth: 200,
    },
    {
      id : 'status',
      label : getI18nFormattedMessage('label.gateway.bundle.details.page.deployments.status.title'),
      value : renderStatus,
      minWidth: 200,
    },
    {
      id : 'deploymentActions',
      label : getI18nFormattedMessage('label.gateway.bundle.details.page.deployments.actions.title'),
      value : renderActions,
      minWidth: 200,
    },
  ]

  const renderProxyName = ({ proxyUuid, proxyName }) =>
    <Link href={`/publish/api-proxy/details/${proxyUuid}`}>{proxyName}</Link>

  const renderStatus = ({ proxyUuid, status, message, deployedGatewayBundleVersion }) => {
    const isValidationError = !!getDeploymentError(proxyUuid);
    const statusLensColor = isValidationError ? getDeployedStatusColor(DEPLOYMENT_STATUS_ERROR) :
      getDeployedStatusColor(status);
    const updatingProxyLensColor = getDeployedStatusColor(DEPLOYMENT_STATUS_NOT_DEPLOYED)
    return (
      <Typography className={classes.cellContainer} data-apim-test={`${pageId}-status`}>
      {
        isUpdating(proxyUuid) ?
          <span className={classes.deploymentStatusContainer}>
            <LensIcon className={classes.statusIcon} style={{ color : updatingProxyLensColor }} data-apim-test={`${pageId}-status-icon`}/>
            {` ${getI18nMessage('label.updating.status.check.in')} ${timer}${getI18nMessage('label.unit.seconds')}. `}
            <Button
              onClick={() => stopAction()}
              data-apim-test={`${pageId}-check-now-button`}>
              {getI18nMessage('label.check.now')}
            </Button>
          </span> :
          <span className={classes.deploymentStatusContainer}>
            <LensIcon className={classes.statusIcon} style={{ color : statusLensColor }} data-apim-test={`${pageId}-status-icon`}/>
            {isValidationError ? getI18nMessage('error.validation.error'): getDeployedStatusLabel(status)}
            {deployedGatewayBundleVersion &&
              ` ${getI18nMessage('label.gateway.bundle.details.page.deployments.status.version', {
                  version : deployedGatewayBundleVersion,
              })}`
            }
            {
              (!isUpdating(proxyUuid) && (getDeploymentError(proxyUuid) || message)) &&
              <Button
                onClick={() =>
                  openStatusDialog(proxyUuid)}
                data-apim-test={`${pageId}-more-info-button`}>
                {getI18nMessage('label.more.info')}
              </Button>
            }
          </span>
      }
      </Typography>
    )
  }

  const renderActions = ({ proxyUuid, status, deployedGatewayBundleVersion }) =>
    <Fragment>
      {!isUpdating(proxyUuid) &&
        <Typography className={classes.cellContainer} data-apim-test={`${pageId}-deployment-actions`}>
          <span className={classes.deploymentButtonsContainer}>
          { status !== DEPLOYMENT_STATUS_NOT_DEPLOYED && undeployButton(proxyUuid) }
          {
            latestGatewayBundleVersion && deployedGatewayBundleVersion &&
            latestGatewayBundleVersion !== deployedGatewayBundleVersion &&
            !(isUpdating(proxyUuid)) &&
            updateButton(proxyUuid)
          }
          { status === DEPLOYMENT_STATUS_NOT_DEPLOYED && deployButton(proxyUuid) }
          {
            (status !== DEPLOYMENT_STATUS_NOT_DEPLOYED &&
              status !== DEPLOYMENT_STATUS_UNDEPLOYED_ERROR) &&
            (!deployedGatewayBundleVersion ||
              latestGatewayBundleVersion === deployedGatewayBundleVersion) &&
            redeployButton(proxyUuid)
          }
          </span>
        </Typography>
      }
    </Fragment>

  const updateButton = (proxyUuid) =>
    <Button
      startIcon={<ArrowForwardIcon data-apim-test={`${pageId}-update-button`}/>}
      endIcon={<Tooltip
        data-apim-test={`${pageId}-update-button-tooltip`}
        title={intl.getI18nMessage("label.gateway.bundle.details.page.deployments.update.latest.version", {
          version : latestGatewayBundleVersion,
        })}
        arrow placement="right">
        <InfoOutlinedIcon/>
      </Tooltip>}
      data-apim-test={`${pageId}-update-button`}
      onClick={() => openConfirmDialog(DEPLOYMENT_ACTION_TYPE_REDEPLOY, proxyUuid)}
      >
      {getI18nFormattedMessage('label.update')}
    </Button>

  const deployButton = (proxyUuid) =>
    <Button
      startIcon={<ArrowForwardIcon data-apim-test={`${pageId}-deploy-button-icon`}/>}
      data-apim-test={`${pageId}-deploy-button`}
      onClick={() => openConfirmDialog(DEPLOYMENT_ACTION_TYPE_DEPLOY, proxyUuid)}>
      {getI18nFormattedMessage('label.deploy')}
    </Button>

  const undeployButton = (proxyUuid) =>
    <Button
      startIcon={<ArrowBackIcon data-apim-test={`${pageId}-undeploy-button-icon`}/>}
      data-apim-test={`${pageId}-undeploy-button`}
      onClick={() => openConfirmDialog(DEPLOYMENT_ACTION_TYPE_UNDEPLOY, proxyUuid)}>
      {getI18nFormattedMessage('label.undeploy')}
    </Button>

  const redeployButton = (proxyUuid) =>
    <Button
      startIcon={<AutorenewIcon data-apim-test={`${pageId}-redeploy-button-icon`}/>}
      data-apim-test={`${pageId}-redeploy-button`}
      onClick={()=> openConfirmDialog(DEPLOYMENT_ACTION_TYPE_REDEPLOY, proxyUuid)}>
      {getI18nFormattedMessage('label.redeploy')}
    </Button>

  const getDeploymentError = (proxyUuid) =>
    get(deploymentErrors.find(err => (err.proxyUuid === proxyUuid)), 'errorMessage');

  const startAction = (action, gatewayBundleUuid, proxyUuid) => {
    switch (action) {
      case DEPLOYMENT_ACTION_TYPE_DEPLOY: {
        deployGatewayBundle(gatewayBundleUuid, proxyUuid);
        break;
      }
      case DEPLOYMENT_ACTION_TYPE_REDEPLOY: {
        redeployGatewayBundle(gatewayBundleUuid, proxyUuid);
        break;
      }
      case DEPLOYMENT_ACTION_TYPE_UNDEPLOY: {
        undeployGatewayBundle(gatewayBundleUuid, proxyUuid);
        break;
      }
      default: {
        break;
      }
    }
    setProxyUpdating(proxyUuid);
  };

  const isUpdating = (proxyUuid) => proxyUpdating === proxyUuid;

  const stopAction = () => {
    setTimer(refreshTime);
    setProxyUpdating('');
    checkStatus(selectedProxy);
  };

  useEffect(() => {
    let interval = null;
    if (!isEmpty(proxyUpdating)) {
      interval = setInterval(() => {
        setTimer(timer - 1);
        if (timer === 1) {
          stopAction();
          clearInterval(interval);
        }
      }, 1000);
    } else if (isEmpty(proxyUpdating) && timer !== refreshTime) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [proxyUpdating, timer]);

  const openStatusDialog = (proxyUuid) => {
    setSelectedProxy(proxyUuid)
    setShowStatusDialog(true)
  }

  const closeStatusDialog = () => {
    setSelectedProxy('');
    setShowStatusDialog(false);
  }

  const openConfirmDialog = (action, proxyUuid) => {
    setSelectedProxy(proxyUuid);
    setShowConfirmDialog(true);
    setConfirmAction(action);
  };

  const closeConfirmDialog = () => {
    setSelectedProxy('');
    setShowConfirmDialog(false);
    setConfirmAction('');
  };

  const saveConfirmDialog = () => {
    startAction(confirmAction, gatewayBundleDetails.uuid, selectedProxy);
    closeConfirmDialog();
  };

  const getDeploymentMessage = (proxyUuid) => {
   const deploymentMessage = get(deploymentProxyList.find((proxy) => proxy.proxyUuid === proxyUuid), 'message');
   return formatStatusMessage(deploymentMessage);
  }

  return (
    <Fragment>
      {showStatusDialog &&
        <AlertDialog
          isOpen={showStatusDialog}
          title={intl.getI18nMessage('label.information')}
          description={getDeploymentError(selectedProxy) || getDeploymentMessage(selectedProxy)}
          submitText={intl.getI18nMessage('label.ok.button')}
          cancelText={intl.getI18nMessage('label.cancel.button')}
          onClose={() => closeStatusDialog()}
          onSubmit={() => closeStatusDialog()}
          onCancel={() => closeStatusDialog()}
          showCancel={false}
          dialogId={`${pageId}-status-dialog`}
        />
      }
      {showConfirmDialog &&
        <AlertDialog
          isOpen={showConfirmDialog}
          title={intl.getI18nMessage('warning.status')}
          description={intl.getI18nMessage('label.action.confirmation.text')}
          submitText={intl.getI18nMessage('label.ok.button')}
          cancelText={intl.getI18nMessage('label.cancel.button')}
          onClose={() => closeConfirmDialog()}
          onSubmit={() => saveConfirmDialog()}
          onCancel={() => closeConfirmDialog()}
          dialogId={`${pageId}-action-dialog`}
        />
      }
      <ListContainer
        listPageId={`${pageId}-table`}
        isLoading={false}
        onAdd={false}
        showListHeader={false}
        pageFilterAndSortClass={classes.filterSection}
        filterAndSortContent={(
          <Fragment>
            <FilterByName
              name={intl.getI18nMessage('label.filter.by')}
              fieldContainerClass={classes.fieldContainer}
              value={filterByName}
              placeholder={intl.getI18nMessage('label.deployments.filter.by.name.placeholder')}
              handleChange={onFilterByNameChange}
              onKeyPress={onFilterByNameKeyPress}
            />
            <FilterByDeploymentState
              name={intl.getI18nMessage('label.filter.by')}
              fieldContainerClass={classes.fieldContainer}
              value={filterByState}
              handleChange={onFilterByStateChange}
              selectFieldClass={classes.selectField}
              hideLabel
            />
          </Fragment>
        )}
        columns={getColumns()}
        rows={deploymentProxyList}
        noResultsMessage={intl.getI18nMessage('label.no.results')}
        page={page}
        totalElements={totalElements}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        onChangeRowsPerPage={onChangeRowsPerPage}
        onChangePage={onChangePage}
        onChangePreviousPage={onChangePreviousPage}
        onChangeNextPage={onChangeNextPage}
      />
    </Fragment>
  );
};

DeploymentTable.propTypes = {
  deploymentProxyList : PropTypes.array,
  gatewayBundleDetails : PropTypes.object,
  deploymentErrors : PropTypes.array,
  refreshTime: PropTypes.string,
  deployGatewayBundle: PropTypes.func,
  redeployGatewayBundle: PropTypes.func,
  undeployGatewayBundle: PropTypes.func,
  checkStatus: PropTypes.func,
  classes: PropTypes.object,
  filterByState: PropTypes.func,
  onFilterByStateChange: PropTypes.func,
  filterByName: PropTypes.func,
  onFilterByNameChange: PropTypes.func,
  onFilterByNameKeyPress: PropTypes.func,
  page: PropTypes.number,
  totalElements : PropTypes.number,
  totalPages : PropTypes.number,
  rowsPerPage : PropTypes.number,
  onChangeRowsPerPage : PropTypes.func,
  onChangePage : PropTypes.func,
  onChangePreviousPage : PropTypes.func,
  onChangeNextPage : PropTypes.func,
};

export default withStyles(styles)(DeploymentTable);
